import {
  DollarSign, ShoppingBag, MousePointerClick, Eye, Globe, XCircle,
  FileText, TrendingUp, Clock, RefreshCw, AlertTriangle, HelpCircle,
  Activity, Smartphone, Apple,
} from "lucide-react";

/**
 * Admin panel for /nabd (نبض السوق AI) funnel.
 * Theme matches the landing page: deep midnight + amber-gold + mint pulse.
 * Provides revenue/orders KPIs, fulfilment metrics (Android downloads + iOS
 * App Store opens) unique to this funnel, geo breakdown, recent paid orders,
 * unpaid attempts with decline-codes, and the Stripe reconcile button.
 */

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    className="rounded-2xl p-6 border"
    style={{
      background: "linear-gradient(180deg,#0E141C 0%,#080C12 100%)",
      borderColor: "rgba(244,184,66,0.22)",
    }}
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs tracking-[0.2em] text-[#9AA5B1]">{label}</span>
      <Icon className="w-5 h-5 text-[#F4B842]" strokeWidth={1.5} />
    </div>
    <div className="text-3xl sm:text-4xl font-black text-white tabular-nums">{value}</div>
    {sub && <div className="text-xs text-[#6B7480] mt-2">{sub}</div>}
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export const NabdPanel = ({ stats, onReconcile, reconciling, reconcileResult }) => {
  const s = stats || {};
  return (
    <div className="max-w-7xl mx-auto space-y-8" dir="ltr" data-testid="nabd-panel">
      {/* Reconcile bar */}
      <div
        className="rounded-2xl p-5 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between border"
        style={{
          background: "linear-gradient(180deg,#0E141C 0%,#080C12 100%)",
          borderColor: "rgba(244,184,66,0.35)",
        }}
        data-testid="nabd-reconcile-bar"
      >
        <div className="flex-1">
          <div className="text-[11px] tracking-[0.22em] text-[#F4B842] font-bold mb-1 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5" /> PAYMENT RECONCILIATION
          </div>
          <div className="text-[13px] text-[#9AA5B1] leading-relaxed">
            Asks Stripe directly about every unpaid نبض السوق AI order — recovers any where the webhook never arrived (flipping the order to paid + firing TikTok Purchase) and surfaces decline reasons for failed cards.
          </div>
          {reconcileResult && (
            <div className="mt-2 text-[12.5px] text-[#FFD27A]" data-testid="nabd-reconcile-result">
              Checked <span className="font-bold">{reconcileResult.summary?.checked || 0}</span> ·
              Fixed <span className="font-bold text-white">{reconcileResult.summary?.fixed || 0}</span> ·
              Failed <span className="font-bold">{reconcileResult.summary?.failed || 0}</span> ·
              Expired <span className="font-bold">{reconcileResult.summary?.expired || 0}</span> ·
              Still open <span className="font-bold">{reconcileResult.summary?.still_open || 0}</span>
            </div>
          )}
        </div>
        <button
          onClick={onReconcile}
          disabled={reconciling}
          data-testid="nabd-reconcile-btn"
          className="text-[#06090F] font-extrabold text-[13px] tracking-wide px-5 py-3 rounded-full transition-all flex items-center justify-center gap-2 min-h-[44px] disabled:opacity-60 shadow-[0_10px_30px_-12px_rgba(244,184,66,0.6)] shrink-0"
          style={{ background: "linear-gradient(180deg,#FFD27A,#F4B842 70%,#D89B23)" }}
        >
          <RefreshCw className={`w-4 h-4 ${reconciling ? "animate-spin" : ""}`} />
          {reconciling ? "Revising with Stripe…" : "Revise payments with Stripe"}
        </button>
      </div>

      {/* Decline codes (only after a reconcile run that surfaced errors) */}
      {reconcileResult?.decline_codes?.length > 0 && (
        <div
          className="rounded-2xl p-5 border"
          style={{ background: "#0E141C", borderColor: "rgba(239,107,107,0.25)" }}
          data-testid="nabd-decline-codes"
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[#EF6B6B]" />
            <h3 className="text-[13px] font-bold tracking-wider text-white">DECLINE CODES (last reconcile)</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {reconcileResult.decline_codes.map(([code, count]) => (
              <span key={code} className="text-[12px] bg-[rgba(239,107,107,0.10)] border border-[rgba(239,107,107,0.30)] text-[#FCA5A5] px-2.5 py-1 rounded-sm">
                <span className="font-mono">{code}</span>{" "}
                <span className="font-bold text-white">×{count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Top-line KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <Kpi
          icon={DollarSign}
          label="REVENUE"
          value={`${(s.revenue || 0).toFixed(0)} ${s.currency || "SAR"}`}
          sub={`${s.orders_paid || 0} paid orders`}
          testid="nabd-stat-revenue"
        />
        <Kpi
          icon={ShoppingBag}
          label="ORDERS"
          value={`${s.orders_paid || 0} / ${s.orders_total || 0}`}
          sub={`${s.checkout_conversion_pct || 0}% checkout conversion`}
          testid="nabd-stat-orders"
        />
        <Kpi
          icon={Eye}
          label="PAGE VISITS"
          value={s.visits || 0}
          sub="landing views"
          testid="nabd-stat-visits"
        />
        <Kpi
          icon={TrendingUp}
          label="VISIT → PAID"
          value={`${s.purchase_conversion_pct || 0}%`}
          sub={`${s.orders_paid || 0} buyers / ${s.visits || 0} visits`}
          testid="nabd-stat-conversion"
        />
      </div>

      {/* Engagement KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <Kpi
          icon={MousePointerClick}
          label="CTA CLICKS"
          value={s.cta_clicks || 0}
          sub={`${s.ctr_pct || 0}% of visits`}
          testid="nabd-stat-cta"
        />
        <Kpi
          icon={HelpCircle}
          label="FAQ OPENS"
          value={s.faq_opens || 0}
          sub="engagement signal"
          testid="nabd-stat-faq"
        />
        <Kpi
          icon={Clock}
          label="AVG TIME ON SITE"
          value={`${Math.floor((s.avg_time_on_site_sec || 0) / 60)}m ${(s.avg_time_on_site_sec || 0) % 60}s`}
          sub="per landing visit"
          testid="nabd-stat-tos"
        />
        <Kpi
          icon={FileText}
          label="PRICE"
          value={`${s.price || 149} ${s.currency || "SAR"}`}
          sub={`was ${s.price_original || 199} SAR`}
          testid="nabd-stat-price"
        />
      </div>

      {/* Fulfilment KPIs — unique to this funnel (app delivery) */}
      <div className="grid grid-cols-2 gap-4 sm:gap-5" data-testid="nabd-fulfilment-kpis">
        <Kpi
          icon={Smartphone}
          label="ANDROID DOWNLOADS"
          value={s.android_downloads || 0}
          sub="success page → APK download"
          testid="nabd-stat-android"
        />
        <Kpi
          icon={Apple}
          label="iOS APP STORE OPENS"
          value={s.ios_opens || 0}
          sub="success page → App Store"
          testid="nabd-stat-ios"
        />
      </div>

      {/* Geo split */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div
          className="rounded-2xl p-6 border"
          style={{ background: "#0E141C", borderColor: "rgba(244,184,66,0.22)" }}
          data-testid="nabd-payment-countries"
        >
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[#F4B842]" />
            <h2 className="text-sm font-bold tracking-wider text-white">PAYMENTS BY COUNTRY</h2>
          </div>
          {(s.payment_countries || []).length === 0 ? (
            <div className="text-sm text-[#6B7480]">No payments yet.</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {(s.payment_countries || []).map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-bold">{c.country || "Unknown"}</span>
                  <span className="text-[#9AA5B1]">
                    {c.count} ·{" "}
                    <span className="text-[#F4B842] font-bold">
                      {(c.revenue || 0).toFixed(0)} {s.currency || "SAR"}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          className="rounded-2xl p-6 border"
          style={{ background: "#0E141C", borderColor: "rgba(244,184,66,0.22)" }}
          data-testid="nabd-visit-countries"
        >
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[#FFD27A]" />
            <h2 className="text-sm font-bold tracking-wider text-white">VISITS BY COUNTRY</h2>
          </div>
          {(s.visit_countries || []).length === 0 ? (
            <div className="text-sm text-[#6B7480]">No tracked visits yet.</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {(s.visit_countries || []).map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-bold">{c.country}</span>
                  <span className="text-[#9AA5B1]">{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Unpaid attempts */}
      <div
        className="rounded-2xl p-6 border"
        style={{ background: "#0E141C", borderColor: "rgba(244,184,66,0.22)" }}
        data-testid="nabd-unpaid"
      >
        <div className="flex items-center gap-2 mb-4">
          <XCircle className="w-4 h-4 text-[#EF6B6B]" />
          <h2 className="text-sm font-bold tracking-wider text-white">
            PAYMENTS NOT PROCESSED (LAST {(s.unpaid_attempts || []).length})
          </h2>
        </div>
        {(s.unpaid_attempts || []).length === 0 ? (
          <div className="text-sm text-[#6B7480]">No unpaid checkout attempts.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] tracking-wider text-[#9AA5B1] border-b border-white/10">
                <th className="py-2 pr-3">WHEN</th>
                <th className="py-2 pr-3">EMAIL</th>
                <th className="py-2 pr-3">COUNTRY</th>
                <th className="py-2 pr-3">STATUS</th>
                <th className="py-2">REASON</th>
              </tr>
            </thead>
            <tbody>
              {(s.unpaid_attempts || []).map((a, i) => (
                <tr key={i} className="border-b border-white/5 align-top">
                  <td className="py-2.5 pr-3 text-[#9AA5B1] whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="py-2.5 pr-3 text-white">{a.email || "—"}</td>
                  <td className="py-2.5 pr-3 text-white">{a.country || "—"}</td>
                  <td className="py-2.5 pr-3 text-white">{a.stripe_status || a.status || "—"}</td>
                  <td className="py-2.5 text-[#9AA5B1] text-[12px]">
                    {a.fail_decline_code
                      ? <span className="text-[#FCA5A5] font-mono">{a.fail_decline_code}</span>
                      : (a.reason || "Not checked yet")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent paid orders */}
      <div
        className="rounded-2xl p-6 border"
        style={{ background: "#0E141C", borderColor: "rgba(244,184,66,0.22)" }}
        data-testid="nabd-recent-orders"
      >
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-[#F4B842]" />
          <h2 className="text-sm font-bold tracking-wider text-white">
            RECENT PAID ORDERS ({(s.recent_orders || []).length})
          </h2>
        </div>
        {(s.recent_orders || []).length === 0 ? (
          <div className="text-sm text-[#6B7480]">No paid orders yet.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] tracking-wider text-[#9AA5B1] border-b border-white/10">
                <th className="py-2 pr-3">WHEN</th>
                <th className="py-2 pr-3">EMAIL</th>
                <th className="py-2 pr-3">COUNTRY</th>
                <th className="py-2">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {(s.recent_orders || []).map((o, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-2.5 pr-3 text-[#9AA5B1] whitespace-nowrap">{fmtDate(o.paid_at)}</td>
                  <td className="py-2.5 pr-3 text-white">{o.email || "—"}</td>
                  <td className="py-2.5 pr-3 text-white">{o.country || "—"}</td>
                  <td className="py-2.5 text-[#F4B842] font-bold">
                    {((o.amount_paid || 0) / 100).toFixed(0)} {(o.currency || "SAR").toUpperCase()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[11px] text-[#6B7480] flex items-center gap-2">
        <Activity className="w-3.5 h-3.5" />
        نبض السوق AI · replyrescue.app/nabd · 149 SAR launch · Stripe Checkout (LIVE)
      </div>
    </div>
  );
};
